import { useState, useEffect, useRef } from 'react';
import Navigation from '../components/Navigation';
import { api } from '../App';

const Ranchette = ({ user, logout }) => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const scriptRefs = useRef([]);
  const styleRefs = useRef([]);

  useEffect(() => {
    fetchBlocks();
  }, []);

  const fetchBlocks = async () => {
    try {
      const response = await api.get('/ranch-blocks');
      setBlocks(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ranch blocks:', error);
      setLoading(false);
    }
  };

  // Inject CSS and JavaScript for each block
  useEffect(() => {
    // Clean up previous styles
    styleRefs.current.forEach(style => {
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    });
    styleRefs.current = [];

    // Clean up previous scripts
    scriptRefs.current.forEach(script => {
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    });
    scriptRefs.current = [];

    // Inject CSS styles
    blocks.forEach((block, index) => {
      if (block.css_content) {
        const style = document.createElement('style');
        style.textContent = block.css_content;
        style.id = `ranch-block-style-${index}`;
        document.head.appendChild(style);
        styleRefs.current.push(style);
      }
    });

    // Execute JavaScript
    blocks.forEach((block, index) => {
      if (block.js_content) {
        const script = document.createElement('script');
        script.text = block.js_content;
        script.id = `ranch-block-script-${index}`;
        document.body.appendChild(script);
        scriptRefs.current.push(script);
      }
    });

    return () => {
      // Cleanup on unmount
      styleRefs.current.forEach(style => {
        if (style && style.parentNode) {
          style.parentNode.removeChild(style);
        }
      });
      scriptRefs.current.forEach(script => {
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [blocks]);

  const blocksByPosition = {
    top: blocks.filter(b => b.position === 'top'),
    main: blocks.filter(b => b.position === 'main'),
    sidebar: blocks.filter(b => b.position === 'sidebar'),
    bottom: blocks.filter(b => b.position === 'bottom'),
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
        <Navigation user={user} logout={logout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-center text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{backgroundColor: '#FCFCF8'}}>
      <Navigation user={user} logout={logout} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-4xl sm:text-5xl font-bold mb-6" style={{color: '#D66238'}}>
          Ranchette
        </h1>

        {/* Isometric Ranch Game */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col items-center">
            <iframe
              src="/ranch-game.html"
              title="Isometric Ranchette Game"
              className="w-full rounded-lg shadow-inner"
              style={{
                height: '600px',
                border: '2px solid #6FCFD3',
                maxWidth: '100%'
              }}
            />
          </div>
        </div>

        {/* Top Blocks */}
        {blocksByPosition.top.length > 0 && (
          <div className="mb-8 space-y-6">
            {blocksByPosition.top.map((block) => (
              <div
                key={block.id}
                className="bg-white rounded-xl shadow-lg p-6"
                dangerouslySetInnerHTML={{ __html: block.html_content }}
              />
            ))}
          </div>
        )}

        {/* Main Content with Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Main Blocks */}
          <div className="lg:col-span-2 space-y-6">
            {blocksByPosition.main.map((block) => (
              <div
                key={block.id}
                className="bg-white rounded-xl shadow-lg p-6"
                dangerouslySetInnerHTML={{ __html: block.html_content }}
              />
            ))}
          </div>

          {/* Sidebar Blocks */}
          {blocksByPosition.sidebar.length > 0 && (
            <div className="space-y-6">
              {blocksByPosition.sidebar.map((block) => (
                <div
                  key={block.id}
                  className="bg-white rounded-xl shadow-lg p-6"
                  dangerouslySetInnerHTML={{ __html: block.html_content }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Bottom Blocks */}
        {blocksByPosition.bottom.length > 0 && (
          <div className="space-y-6">
            {blocksByPosition.bottom.map((block) => (
              <div
                key={block.id}
                className="bg-white rounded-xl shadow-lg p-6"
                dangerouslySetInnerHTML={{ __html: block.html_content }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Ranchette;